import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import applicationConstants from '../../../../Core/Utility/ApplicationConstants';
import { DemeterDataFrequency } from '../../../../Generated/Raven-Demeter';
import dailySvg from '../../../Assets/PhysicalPricesTableIcons/daily.svg';
import dailySvgForecast from '../../../Assets/PhysicalPricesTableIcons/dailyForecast.svg';
import monthlySvg from '../../../Assets/PhysicalPricesTableIcons/monthly.svg';
import monthlySvgForecast from '../../../Assets/PhysicalPricesTableIcons/monthlyForecast.svg';
import weeklySvg from '../../../Assets/PhysicalPricesTableIcons/weekly.svg';
import weeklySvgForecast from '../../../Assets/PhysicalPricesTableIcons/weeklyForecast.svg';
import { TableHeader } from '../../../Components/Tables/Table/TableDefinitions';
import formattingService from '../../../Services/Formatting/FormattingService';
import languageService from '../../../Services/Language/LanguageService';
import { translationKeys } from '../../../Services/Language/TranslationDefinitions';
import styleService from '../../../Services/Styles/StyleService';
import { CommodityOtcPriceCompositeModel } from '../../Futures/Otc/OtcPricesTableDefinitions';
import { SymbolContractCompositeModel } from '../../Futures/Prices/FuturesPricesTableDefinitions';
import { PhysicalPricesCompositeModel } from '../../PhysicalPrices/PhysicalPricesPageDefinitions';

export const dashboardFuturesPricesTableDefinitions: TableHeader<SymbolContractCompositeModel>[] = [
    {
        field: 'reutersInstrumentCode',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.symbol),
        pinned: 'left',
        minimumWidth: 100,
    },
    {
        field: 'contractYearMonth',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.contractMonth),
        cellRenderer: (datum) => formattingService.toMonthYear(new Date(datum.year, datum.month - 1, 1)),
        minimumWidth: 120,
    },
    {
        field: 'latestOrSettledPriceConverted',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.last),
        cellRenderer: (datum) => {
            if (!datum.latestOrSettledPriceConverted) {
                return applicationConstants.TablePlaceholderZeroOrEmpty;
            }
            const value = datum.latestOrSettledPriceConverted * datum.multipler;
            const suffix = datum.latestPrice ? '' : 's';
            return `${formattingService.formatPriceNumber(value)}${suffix}`;
        },
        minimumWidth: 75,
    },
    {
        field: 'bidSize',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.bidSize),
        dataFormat: 'number',
        minimumWidth: 75,
    },
    {
        field: 'bidPriceConverted',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.bidPrice),
        cellRenderer: (datum) => formattingService.formatPriceNumber(datum.bidPriceConverted, { multiplier: datum.multipler }),
        minimumWidth: 75,
    },
    {
        field: 'askPriceConverted',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.askPrice),
        cellRenderer: (datum) => formattingService.formatPriceNumber(datum.askPriceConverted, { multiplier: datum.multipler }),
        minimumWidth: 75,
    },
    {
        field: 'askSize',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.askSize),
        dataFormat: 'number',
        minimumWidth: 75,
    },
    {
        field: 'volume',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.volumeTradedToday),
        dataFormat: 'number',
        minimumWidth: 75,
    },
    {
        field: 'netChangeConverted',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.netChange),
        cellRenderer: (datum) => formattingService.formatPriceNumber(datum.netChangeConverted, { multiplier: datum.multipler }),
        minimumWidth: 75,
        handleGetCellClasses: (datum) => [styleService.getPositiveOrNegativeClass(datum.netChangeConverted)],
    },
    {
        field: 'percentChange',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.percentChange),
        dataFormat: 'percent',
        minimumWidth: 75,
        handleGetCellClasses: (datum) => [styleService.getPositiveOrNegativeClass(datum.percentChange)],
    },
];

export const dashboardOtcPricesTableDefinitions: TableHeader<CommodityOtcPriceCompositeModel>[] = [
    {
        field: 'contractMonth',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.contractMonth),
        cellRenderer: (datum) => formattingService.toMonthYear(new Date(datum.contractYear, datum.contractMonth - 1, 1)),
        pinned: 'left',
        minimumWidth: 100,
    },
    {
        field: 'bidPrice',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.bidPrice),
        cellRenderer: (datum) => {
            if (!datum.bidPrice) {
                return applicationConstants.TablePlaceholderZeroOrEmpty;
            }
            return formattingService.formatNumber(datum.bidPrice, { minimumDecimalPlaces: 0, maximumDecimalPlaces: 5 });
        },
        minimumWidth: 75,
    },
    {
        field: 'askPrice',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.askPrice),
        cellRenderer: (datum) => {
            if (!datum.askPrice) {
                return applicationConstants.TablePlaceholderZeroOrEmpty;
            }
            return formattingService.formatNumber(datum.askPrice, { minimumDecimalPlaces: 0, maximumDecimalPlaces: 5 });
        },
        minimumWidth: 75,
    },
    {
        field: 'releaseTime',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.lastUpdate),
        cellRenderer: (datum) => {
            if (!datum.releaseTime) {
                return applicationConstants.TablePlaceholderZeroOrEmpty;
            }
            return formattingService.toTimestampFromUtc(datum.releaseTime);
        },
        minimumWidth: 150,
    },
    {
        field: 'percentChange',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.percentChange),
        dataFormat: 'percent',
        minimumWidth: 75,
        handleGetCellClasses: (datum) => [styleService.getPositiveOrNegativeClass(datum.percentChange)],
    },
    {
        field: 'netChange',
        headerValueGetter: () => languageService.translate(translationKeys.futures.fields.netChange),
        cellRenderer: (datum) => {
            if (!datum.netChange) {
                return applicationConstants.TablePlaceholderZeroOrEmpty;
            }
            return formattingService.formatNumber(datum.netChange, { minimumDecimalPlaces: 0, maximumDecimalPlaces: 5 });
        },
        minimumWidth: 75,
        handleGetCellClasses: (datum) => [styleService.getPositiveOrNegativeClass(datum.netChange)],
    },
];

const iconDefinitionObjects = {
    [DemeterDataFrequency.Daily]: {
        currentTitle: translationKeys.physicalPrices.tooltip.daily,
        forecastTitle: translationKeys.physicalPrices.tooltip.dailyForecast,
        currentSvg: dailySvg,
        forecastSvg: dailySvgForecast,
    },
    [DemeterDataFrequency.Weekly]: {
        currentTitle: translationKeys.physicalPrices.tooltip.weekly,
        forecastTitle: translationKeys.physicalPrices.tooltip.weeklyForecast,
        currentSvg: weeklySvg,
        forecastSvg: weeklySvgForecast,
    },
    [DemeterDataFrequency.Monthly]: {
        currentTitle: translationKeys.physicalPrices.tooltip.monthly,
        forecastTitle: translationKeys.physicalPrices.tooltip.monthlyForecast,
        currentSvg: monthlySvg,
        forecastSvg: monthlySvgForecast,
    },
};

const frequencyIconsContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '5px',
    height: '100%',
};

export const dashboardPhysicalPricesTableDefinitions: TableHeader<PhysicalPricesCompositeModel>[] = [
    {
        field: 'displayName',
        headerValueGetter: () => languageService.translate(translationKeys.physicalPrices.fields.name),
        cellRenderer: (datum) => {
            if (!datum.displayName) {
                return applicationConstants.TablePlaceholderZeroOrEmpty;
            }
            return datum.displayName;
        },
        pinned: 'left',
        minimumWidth: 100,
    },
    {
        field: 'asOfDate',
        headerValueGetter: () => languageService.translate(translationKeys.physicalPrices.fields.date),
        cellRenderer: (datum) => {
            if (!datum.asOfDate) {
                return applicationConstants.TablePlaceholderZeroOrEmpty;
            }
            return formattingService.toShortDayMonthYear(datum.asOfDate);
        },
        minimumWidth: 75,
    },
    {
        field: 'value',
        headerValueGetter: () => languageService.translate(translationKeys.physicalPrices.fields.price),
        cellRenderer: (datum) => {
            if (!datum.value) {
                return applicationConstants.TablePlaceholderZeroOrEmpty;
            }
            return formattingService.formatNumber(datum.value, { minimumDecimalPlaces: 0, maximumDecimalPlaces: 4 });
        },
        minimumWidth: 75,
    },
    {
        field: 'percentChange',
        headerValueGetter: () => languageService.translate(translationKeys.physicalPrices.fields.percentChange),
        dataFormat: 'percent',
        minimumWidth: 75,
        handleGetCellClasses: (datum) => [styleService.getPositiveOrNegativeClass(datum.percentChange)],
    },
    {
        field: 'netChange',
        headerValueGetter: () => languageService.translate(translationKeys.physicalPrices.fields.netChange),
        cellRenderer: (datum) => {
            if (!datum.netChange) {
                return applicationConstants.TablePlaceholderZeroOrEmpty;
            }
            return formattingService.formatNumber(datum.netChange, { minimumDecimalPlaces: 0, maximumDecimalPlaces: 4 });
        },
        minimumWidth: 75,
        handleGetCellClasses: (datum) => [styleService.getPositiveOrNegativeClass(datum.netChange)],
    },
    {
        field: 'priceFrequencyDefinitions',
        headerValueGetter: () => languageService.translate(translationKeys.physicalPrices.fields.frequencyOrForcast),
        cellRenderer: (datum) => {
            const icons = datum.priceFrequencyDefinitions.map((element) => {
                const iconDefinition = iconDefinitionObjects[element.dataFrequency as keyof typeof iconDefinitionObjects];

                if (!iconDefinition) {
                    return null;
                }

                return (
                    <Tooltip
                        key={`${datum.id}_${element.dataFrequency}`}
                        title={
                            element.hasForecastData
                                ? languageService.translate(iconDefinition.forecastTitle)
                                : languageService.translate(iconDefinition.currentTitle)
                        }
                        placement="top-start"
                    >
                        <img src={element.hasForecastData ? iconDefinition.forecastSvg : iconDefinition.currentSvg} alt="#" />
                    </Tooltip>
                );
            });

            return <div style={frequencyIconsContainerStyle}>{icons.reverse()}</div>;
        },
        minimumWidth: 100,
    },
];
